import React, { useRef, useState } from "react";
import { useAdminAccount } from "./useAdminAccount";
import "./AdminAccount.css";

export default function AdminChangePassword() {
  const { loading, loadError, save } = useAdminAccount();

  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [visible, setVisible] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const submitting = useRef(false);

  function update(key, value) {
    setForm((previous) => ({ ...previous, [key]: value }));
  }

  function toggleVisibility(key) {
    setVisible((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  }

  async function submit(event) {
    event.preventDefault();

    if (submitting.current) return;

    if (form.newPassword !== form.confirmPassword) {
      setNotice({
        type: "error",
        text: "New passwords do not match.",
      });
      return;
    }

    submitting.current = true;
    setBusy(true);
    setNotice(null);

    try {
      const data = await save("password", form);

      setForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      setVisible({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false,
      });

      setNotice({ type: "success", text: data.message });
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  return (
    <main className="aa-page">
      <section className="aa-card aa-password-card">
        <header className="aa-heading">
          <span className="aa-heading-icon">
            <i className="bi bi-shield-lock" aria-hidden="true" />
          </span>

          <div>
            <h1>Change Password</h1>
            <p>Enter your current password to choose a new one.</p>
          </div>
        </header>

        {loading ? (
          <p role="status">Loading account…</p>
        ) : loadError ? (
          <div className="aa-notice aa-error" role="alert">
            {loadError}
          </div>
        ) : (
          <form onSubmit={submit}>
            {notice && (
              <div
                className={`aa-notice aa-${notice.type}`}
                role={notice.type === "error" ? "alert" : "status"}
              >
                {notice.text}
              </div>
            )}

            <fieldset disabled={busy}>
              <div className="aa-password-fields">
                {[
                  ["currentPassword", "Current Password"],
                  ["newPassword", "New Password"],
                  ["confirmPassword", "Confirm New Password"],
                ].map(([key, label]) => (
                  <div className="aa-password-field" key={key}>
                    <label htmlFor={`aa-${key}`}>{label}</label>

                    <div className="aa-password-input">
                      <input
                        id={`aa-${key}`}
                        type={visible[key] ? "text" : "password"}
                        required
                        maxLength={
                          key === "currentPassword" ? undefined : 255
                        }
                        autoComplete={
                          key === "currentPassword"
                            ? "current-password"
                            : "new-password"
                        }
                        placeholder={`Enter ${label.toLowerCase()}`}
                        value={form[key]}
                        onChange={(event) =>
                          update(key, event.target.value)
                        }
                      />

                      <button
                        type="button"
                        className="aa-password-eye"
                        aria-label={`${
                          visible[key] ? "Hide" : "Show"
                        } ${label.toLowerCase()}`}
                        aria-controls={`aa-${key}`}
                        onClick={() => toggleVisibility(key)}
                      >
                        <i
                          className={`bi ${
                            visible[key] ? "bi-eye-slash" : "bi-eye"
                          }`}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button type="submit" className="aa-submit">
                <i className="bi bi-lock" aria-hidden="true" />
                {busy ? "Updating…" : "Change Password"}
              </button>
            </fieldset>
          </form>
        )}
      </section>
    </main>
  );
}